import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchCustomDashboards,
  fetchFacilitiesHierarchy,
} from '../../redux/slices/CustomDashboardSlice';
import { fetchListChargingPointFilters } from '../../redux/slices/DeviceManagementSlice';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import BatteryLoader from '../../shared-components/battery-loader/BatteryLoader';
import dashboard from '../../utils/api/dashboard';

import ChargingScheduleChart from './components/ChargingScheduleChart';
import ConnectorStatusChart from './components/ConnectorStatusChart';
import DashboardCards from './components/DashboardCards';
import DashboardMap from './components/DashboardMap';
import DashboardToolbar from './components/DashboardToolbar';
// import DeviceDurationChart from './components/DeviceDurationChart';
import DeviceStatus from './components/DeviceStatus';
import DeviceUtilizationChart from './components/DeviceUtilization';
// import LiveAlerts from './components/LiveAlerts';
import NewStationPerformanceChart from './components/NewStationPerformanceChart';
import PenaltyRevenueChart from './components/PenaltyRevenueChart';
import RevenuePowerConsumedChart from './components/RevenuePowerConsumedChart';
// import StationAlertsChart from './components/StationAlertsChart';
import WorkspaceView from './components/WorkspaceView';

type DurationKey = 'last24hours' | 'last7days' | 'lastMonth' | 'last12months';

/**
 * Dashboard component
 */
function Dashboard() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { filters, filterListLoading } = useAppSelector((s) => s.device);
  const { selectedDashboardId, dashboards } = useAppSelector((state) => state.customDashboard);
  const selectedDashboard = dashboards.find((d) => d.id === selectedDashboardId);
  const [toggleList, setToggleList] = useState([
    {
      label: '24 Hours',
      key: 'last24hours',
      isActive: false,
    },
    {
      label: '7 Days',
      key: 'last7days',
      isActive: true,
    },
    {
      label: 'Last Month',
      key: 'lastMonth',
      isActive: false,
    },
    {
      label: '12 Months',
      key: 'last12months',
      isActive: false,
    },
  ]);
  const [dashboardValue, setDashboardValue] = useState<any>();
  const [loading, setLoading] = useState<boolean>(true);
  const [appliedFilters, setAppliedFilters] = useState<{
    facilityIds: string[];
    stationIds: string[];
    deviceIds: string[];
    type: string[];
  }>({
    facilityIds: [],
    stationIds: [],
    deviceIds: [],
    type: ['commercial'],
  });

  const activeDuration = (toggleList.find((t) => t.isActive)?.key || 'last7days') as DurationKey;

  /**
   * Handles duration toggle click
   * @param key - The key of the clicked duration item
   */
  const handleDurationChange = (key: string) => {
    setToggleList((previous) =>
      previous.map((item) => ({
        ...item,
        isActive: item.key === key,
      }))
    );
  };

  /**
   * Handles filter application from DashboardFilter
   * @param selectedFilters - Selected filter criteria
   * @param selectedFilters.facilityIds - List of selected facility IDs
   * @param selectedFilters.stationIds - List of selected station IDs
   * @param selectedFilters.deviceIds - List of selected device IDs
   * @param selectedFilters.type - Array of selected business types ('commercial', 'personal')
   */
  const handleApplyFilters = (selectedFilters: {
    facilityIds: string[];
    stationIds: string[];
    deviceIds: string[];
    type: string[];
  }) => {
    setAppliedFilters(selectedFilters);
  };

  useEffect(() => {
    setLoading(true);
    dashboard.getDefaultCustomDashboard().then((response: any) => {
      setDashboardValue(response?.data);
      setLoading(false);
    }).catch((error: any) => {
      console.error('error', error);
      setLoading(false);
      dispatch(setNotificationMessage({
        status: true,
        message: error?.response?.data?.message || 'Failed to fetch default dashboard',
        type: 'error',
      }));
    });
    const filterWithOutSearchBy = filters.filter((eachFilter) => eachFilter.key !== 'searchBy');
    if (filterWithOutSearchBy.length === 0 && !filterListLoading) {
      dispatch(fetchListChargingPointFilters());
    }
    dispatch(setPageTitle(''));
    dispatch(fetchCustomDashboards({}));
    dispatch(fetchFacilitiesHierarchy());
  }, []);
  if (loading) {
    return (
      <div className="dashboard-wrap-loader">
        <BatteryLoader />
      </div>
    );
  }

  return (
    <div className="dashboard-wrap">
      <DashboardToolbar
        onCustomize={() => navigate('/ev-admin/dashboard/customize-dashboard')}
        toggleList={toggleList}
        handleClickFunction={handleDurationChange}
        onApply={handleApplyFilters}
        onClear={() => setAppliedFilters({
          facilityIds: [],
          stationIds: [],
          deviceIds: [],
          type: ['commercial'],
        })}
        filters={appliedFilters}
      />
      {(!dashboardValue || Object.keys(dashboardValue).length === 0) && selectedDashboardId === 'default' ? (
        <>
          <DashboardCards dateDuration={activeDuration} filters={appliedFilters} />
          <div className="dashboard-content">
            <div className="map-bar-chart">
              <DashboardMap filters={appliedFilters} />
              <DeviceStatus filters={appliedFilters} />
              {/* <DeviceDurationChart /> */}
              {/* <LiveAlerts filters={appliedFilters} /> */}
            </div>
            <div className="performance-connector-device-status">
              <NewStationPerformanceChart filters={appliedFilters} />
              <ConnectorStatusChart filters={appliedFilters} />
            </div>
            {/* <StationOverview /> */}
            <div className="revenue-station-alert-wrap">
              <RevenuePowerConsumedChart dateDuration={activeDuration} filters={appliedFilters} />
              <PenaltyRevenueChart dateDuration={activeDuration} filters={appliedFilters} />
              {/* <StationAlertsChart dateDuration={activeDuration} filters={appliedFilters} /> */}
              {/* <ChargerStatusChart /> */}
            </div>
            <div className="device-related-chart">
              <ChargingScheduleChart dateDuration={activeDuration} filters={appliedFilters} />
              <DeviceUtilizationChart dateDuration={activeDuration} filters={appliedFilters} />
            </div>
            {/* <div className="utilization-chart">
              <DeviceUtilizationChart dateDuration={activeDuration} filters={appliedFilters} />
            </div> */}
          </div>
        </>
      ) : (
        <WorkspaceView dateDuration={activeDuration} filters={appliedFilters} selectedDashboard={selectedDashboardId === 'default' ? dashboardValue : selectedDashboard} />
      )}
    </div>
  );
}

export default Dashboard;
